import {computed, DOCUMENT, inject, Injectable, signal, Signal, WritableSignal} from '@angular/core';
import Bowser from 'bowser';
import {environment} from '../../../environments/environment';
import {Capacitor} from '@capacitor/core';
import {ElectronService} from '../electron/electron.service';
import {Device} from '@capacitor/device';
import {SystemInfoAppTypeEnum} from '../../enums/system-info/system-info-app-type.enum';
import {SystemInfo} from '../../models/system-info.model';
import {SystemInfoOsNameEnum} from '../../enums/system-info/system-info-os-name.enum';
import {ServerSelectionService} from '../server-selection/server-selection.service';
import {ServerAddress} from '../../models/server-address.model';
import {MIN_SUPPORTED_BROWSERS} from '../../../configs/browser-support.generated';
import ParsedResult = Bowser.Parser.ParsedResult;

@Injectable({
    providedIn: 'root'
})
export class SystemInfoService {
    private readonly serverSelectionService = inject(ServerSelectionService);
    private readonly window = inject(DOCUMENT).defaultView ?? window;
    private readonly systemInfo: Signal<SystemInfo>;
    private readonly deviceSignal: WritableSignal<string | null>;
    private readonly selectedServerSignal: Signal<ServerAddress | undefined>;

    public constructor() {
        let bowserSystemInfo: ParsedResult | null = null;
        if (this.window.navigator?.userAgent) {
            bowserSystemInfo = Bowser.parse(this.window.navigator.userAgent);
        }

        this.selectedServerSignal = this.serverSelectionService.getSelectedServerSignal();
        this.deviceSignal = signal(null);

        this.systemInfo = computed(() => ({
            os: bowserSystemInfo?.os.name ?? null,
            osVersion: bowserSystemInfo?.os.version ?? null,
            device: this.deviceSignal(),
            browser: bowserSystemInfo?.browser.name ?? null,
            browserVersion: bowserSystemInfo?.browser.version ?? null,
            appType: this.detectAndGetAppType(),
            appVersion: environment.version ?? null,
            apiVersion: this.selectedServerSignal()?.serverInformation?.apiVersion ?? null,
            screenResolution: this.getScreenResolution(),
        }));
    }

    public async init(): Promise<void> {
        await this.setDeviceInfo();
    }

    /**
     * Retrieves the system information, including device details.
     * If the device information is not already set, it triggers a process to fetch and store the device details before returning the system information.
     *
     * @return {Promise<SystemInfo>} A promise that resolves to the system information object.
     */
    public async getSystemInfo(): Promise<SystemInfo> {
        if (this.systemInfo().device === null) {
            await this.setDeviceInfo();
            return this.systemInfo();
        } else {
            return this.systemInfo();
        }
    }

    /**
     * Determines whether the current application is a web application.
     *
     * @return {boolean} True if the application is a web app, otherwise false.
     */
    public isWebApp(): boolean {
        return this.systemInfo().appType === SystemInfoAppTypeEnum.WEB_APP;
    }

    /**
     * Checks if the application is running in an Electron environment.
     *
     * @return {boolean} Returns true if the application is running in Electron, otherwise false.
     */
    public isElectron(): boolean {
        return this.systemInfo().appType === SystemInfoAppTypeEnum.ELECTRON;
    }

    /**
     * Determines if the current application type is Capacitor.
     *
     * @return {boolean} True if the application type is Capacitor, otherwise false.
     */
    public isCapacitor(): boolean {
        return this.systemInfo().appType === SystemInfoAppTypeEnum.CAPACITOR;
    }

    /**
     * Determines if the current operating system is iOS.
     *
     * @return {boolean} True if the operating system is iOS, false otherwise.
     */
    public isIos(): boolean {
        return this.systemInfo().os === SystemInfoOsNameEnum.IOS;
    }

    /**
     * Determines if the operating system is Android.
     *
     * @return {boolean} Returns true if the operating system is Android, otherwise false.
     */
    public isAndroid(): boolean {
        return this.systemInfo().os === SystemInfoOsNameEnum.ANDROID;
    }

    /**
     * Checks if the operating system is macOS.
     *
     * @return {boolean} True if the operating system is macOS, otherwise false.
     */
    public isMacOs(): boolean {
        return this.systemInfo().os === SystemInfoOsNameEnum.MACOS;
    }

    /**
     * Determines if the operating system is Windows.
     *
     * @return {boolean} Returns true if the operating system is Windows, otherwise false.
     */
    public isWindows(): boolean {
        return this.systemInfo().os === SystemInfoOsNameEnum.WINDOWS;
    }

    /**
     * Determines whether the current browser is new enough to run the app.
     *
     * Unknown or unlisted browsers are treated as supported so nobody is wrongly locked out; only
     * an explicit version mismatch (Bowser's `satisfies` returning `false`) counts as too old.
     *
     * @return {boolean} True if the browser meets the supported version floor, otherwise false.
     */
    public isSupportedBrowser(): boolean {
        const userAgent = this.window.navigator?.userAgent;
        if (!userAgent) {
            return true;
        }

        return Bowser.getParser(userAgent)
            .satisfies({ ...MIN_SUPPORTED_BROWSERS }) !== false;
    }

    /**
     * Determines the screen resolution using the Screen API (e.g. "1920x1080").
     * Falls back to null when the Screen API is unavailable (e.g. non-browser environments).
     *
     * @return {string | null} The screen resolution as "<width>x<height>", or null if unavailable.
     */
    public getScreenResolution(): string | null {
        const screen = this.window.screen;
        if (!screen) {
            return null;
        }

        return `${screen.width}x${screen.height}`;
    }

    private detectAndGetAppType(): SystemInfoAppTypeEnum | null {
        if (ElectronService.isElectronApp()) {
            return SystemInfoAppTypeEnum.ELECTRON;
        }

        const platform = Capacitor.getPlatform();

        if (platform === 'web') {
            return SystemInfoAppTypeEnum.WEB_APP;
        }

        if (Capacitor.isNativePlatform()) {
            return SystemInfoAppTypeEnum.CAPACITOR;
        }

        return null;
    }

    private async setDeviceInfo(): Promise<void> {
        const deviceInfo = await Device.getInfo();

        this.deviceSignal.set(deviceInfo.model ?? null);
    }
}
